import React, { useState, useEffect } from 'react';
import { Building2, RefreshCw, Loader } from 'lucide-react';
import { organizationsService } from '../../services/organizations';
import { Organization, OrganizationStatus } from '../../types';

export const OrganizationManagement: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setRefreshing(true);
      const data = await organizationsService.getAll();
      setOrganizations(data);
      setError(null);
    } catch (err: any) {
      console.error('Erreur lors du chargement des organisations:', err);
      setError(err.message || 'Une erreur est survenue lors du chargement des organisations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const statusStyles: Record<OrganizationStatus, string> = {
    active: 'bg-mint-container text-mint rounded-full px-3 py-1 text-xs font-medium',
    inactive: 'bg-cream-high text-ink-soft rounded-full px-3 py-1 text-xs font-medium',
  };

  const statusLabels: Record<OrganizationStatus, string> = {
    active: 'Actif',
    inactive: 'Inactif',
  };

  return (
    <div className="bg-white shadow-ambient rounded-2xl p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-ink">Organisations</h2>
          <button
            onClick={fetchOrganizations}
            disabled={refreshing}
            className="p-1 text-ink-soft hover:text-coral disabled:opacity-50"
            title="Rafraîchir la liste"
          >
            {refreshing ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <RefreshCw className="w-5 h-5" />
            )}
          </button>
        </div>
        <span className="text-sm text-ink-soft">
          {organizations.length} organisation{organizations.length > 1 ? 's' : ''}
        </span>
      </div>

      {error && (
        <div className="bg-primary-container text-coral rounded-xl px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
        </div>
      ) : organizations.length === 0 ? (
        <div className="text-center py-14 px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gold-container/50 flex items-center justify-center">
            <Building2 size={32} className="text-gold" />
          </div>
          <h3 className="text-lg font-semibold text-ink mb-1">
            Aucune organisation trouvée
          </h3>
          <p className="text-sm text-ink-soft max-w-sm mx-auto">
            La gestion des organisations se fait depuis le LMS.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-cream-high">
                <th className="px-6 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-[0.05em]">Organisation</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-[0.05em]">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-[0.05em]">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-high">
              {organizations.map((org) => (
                <tr key={org.id} className="hover:bg-cream/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {org.logoUrl ? (
                        <img src={org.logoUrl} alt={org.name} className="w-8 h-8 rounded-lg object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-lavender-container flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-lavender" />
                        </div>
                      )}
                      <span className="font-medium text-ink">{org.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-ink-soft">
                    {org.contactEmail || '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={statusStyles[org.status] || statusStyles.inactive}>
                      {statusLabels[org.status] || org.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
